import React, { useState } from "react";
import "../styles/cancelSubscriptionModal.scss";

/**
 * CancelSubscriptionModal — Two-step modal flow (Survey → Confirmation)
 *
 * Step 1 — Cancel Survey:
 *   - Header with title and close button
 *   - Subtitle explaining the survey
 *   - 7 radio button options for cancellation reasons
 *   - Two buttons: "Yes, I'm sure" and "Cancel"
 *
 * Step 2 — Confirmation:
 *   - Confirmation message with subscription end date
 *   - "Done" button to close
 *
 * @param {boolean} isOpen — show/hide modal
 * @param {function} onClose — close handler
 * @param {function} onConfirm — called with selected reason when user confirms cancellation
 * @param {string} userName — user's display name
 * @param {string} endDate — subscription end date string
 * @param {string} email — user's email for confirmation message
 */

const SURVEY_OPTIONS = [
  "No longer need this site",
  "It's too expensive",
  "Issue with this site",
  "I need to take a break",
  "This site doesn't have a feature i need",
  "I doesn't have time to watch shows",
  "Technical Issue",
];

const CancelSubscriptionModal = ({ isOpen, onClose, onConfirm, userName, endDate, email }) => {
  const [step, setStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const displayName = userName || "there";
  const displayDate = endDate || new Date().toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
  const displayEmail = email || "your registered email";

  const handleYesConfirm = async () => {
    if (selectedReason === null || isProcessing) return;

    setIsProcessing(true);
    try {
      // Call the actual cancel API via the onConfirm prop
      const reason = SURVEY_OPTIONS[selectedReason];
      await onConfirm?.(reason);
      setStep(2); // Move to confirmation only after success
    } catch (error) {
      console.error("[CancelModal] Cancel failed:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedReason(null);
    setIsProcessing(false);
    onClose?.();
  };

  const handleDone = () => {
    handleClose();
  };

  return (
    <div className="cancel-subscription-overlay" onClick={handleClose}>
      <div className="cancel-subscription-modal" onClick={(e) => e.stopPropagation()}>
        {step === 1 ? (
          <>
            {/* Step 1: Survey */}
            <div className="cancel-subscription-modal__header">
              <div className="cancel-subscription-modal__header-text">
                <h2 className="cancel-subscription-modal__title">Cancel Subscription</h2>
                <p className="cancel-subscription-modal__subtitle">
                  We are sorry to have to let you go, to help us improve our website, please provide a reason why you unsubscribed from us in the following short survey.
                </p>
              </div>
              <button
                className="cancel-subscription-modal__close"
                onClick={handleClose}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="#525252" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Radio options */}
            <div className="cancel-subscription-modal__survey">
              {SURVEY_OPTIONS.map((option, index) => (
                <label key={index} className="cancel-subscription-modal__radio-option">
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={index}
                    checked={selectedReason === index}
                    onChange={(e) => setSelectedReason(parseInt(e.target.value))}
                    className="cancel-subscription-modal__radio-input"
                  />
                  <span className="cancel-subscription-modal__radio-circle"></span>
                  <span className="cancel-subscription-modal__radio-label">{option}</span>
                </label>
              ))}
            </div>

            {/* Buttons */}
            <div className="cancel-subscription-modal__buttons">
              <button
                className="cancel-subscription-modal__button cancel-subscription-modal__button--outline"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                className="cancel-subscription-modal__button cancel-subscription-modal__button--primary"
                onClick={handleYesConfirm}
                disabled={selectedReason === null || isProcessing}
              >
                {isProcessing ? "Processing..." : "Yes, I'm sure"}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Step 2: Confirmation */}
            <div className="cancel-subscription-modal__header">
              <div className="cancel-subscription-modal__header-text">
                <h2 className="cancel-subscription-modal__title">
                  {displayName}, your subscription has been canceled
                </h2>
              </div>
              <button
                className="cancel-subscription-modal__close"
                onClick={handleDone}
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="#525252" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Confirmation message */}
            <p className="cancel-subscription-modal__confirmation-text">
              Your subscription will be canceled as of {displayDate}, we will send a confirmation email to {displayEmail}.
            </p>

            {/* Done button */}
            <div className="cancel-subscription-modal__buttons cancel-subscription-modal__buttons--single">
              <button
                className="cancel-subscription-modal__button cancel-subscription-modal__button--outline"
                onClick={handleDone}
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
